import React from "react"

import type { LockDisplayData } from "@/domains/lock-page/components/card/0.story-mocks"
import type { TokenStore } from "@/domains/tokens"
import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { Separator } from "@/components/separator"
import { SwitchWithLabel } from "@/domains/common/components/switches/switch-with-label"
import { cn } from "@/domains/library"
import { IncreaseAmountExpiredPanel } from "@/domains/lock-page/components/card/increase-amount-card/composables/increase-amount-expired-panel/increase-amount-expired-panel"
import { LockIncreaseVisualizer } from "@/domains/lock-page/components/card/increase-amount-card/composables/lock-increase-visualizer/lock-increase-visualizer"
import { TokenInputCard } from "@/domains/tokens/components/token-input-card"

export interface IncreaseAmountCardProps {
  currentLock: LockDisplayData
  futureLock: LockDisplayData | null
  amount: string
  governanceToken: TokenStore
  hasExpired: boolean
  canIncrease: boolean
  icon?: React.ReactElement
  onModeChange: (checked: boolean) => void
  onAmountChange: (amount: string) => void
  onIncrease: () => void
}

export const IncreaseAmountCard = ({
  currentLock,
  futureLock,
  amount,
  governanceToken,
  hasExpired,
  canIncrease,
  icon,
  onModeChange,
  onAmountChange,
  onIncrease,
}: IncreaseAmountCardProps) => {
  return (
    <Card.Container
      className={cn("flex w-full flex-col gap-6", "rounded-lg border border-p400-light bg-p400 p-4 lg:p-6")}
    >
      <Card.Header>
        {icon && (
          <Card.HeaderIconContainer>
            {React.cloneElement(icon, { className: "text-t200", size: 24 })}
          </Card.HeaderIconContainer>
        )}

        <div className="flex w-full flex-col">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <Card.HeaderTitle>Increase veSCALE</Card.HeaderTitle>
              <Card.HeaderDescription>Increase locked balance by an amount</Card.HeaderDescription>
            </div>
            <div className="flex items-center">
              <SwitchWithLabel label="Increase mode" checked={false} onCheckedChange={onModeChange} />
            </div>
          </div>
        </div>
      </Card.Header>
      <Separator
        className="relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light
      after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
      after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
        decorative
      />
      {hasExpired ? (
        <IncreaseAmountExpiredPanel />
      ) : (
        <>
          <div className="space-y-4">
            <div className="flex flex-col gap-1">
              {futureLock && <LockIncreaseVisualizer currentLock={currentLock} futureLock={futureLock} />}
            </div>
          </div>

          <TokenInputCard
            amount={amount}
            showTokenSelectable={false}
            token={governanceToken}
            tokens={[]}
            onTokenAmountChange={onAmountChange}
            placeholder="Enter an amount to increase by"
          />
        </>
      )}
      <div className="flex justify-end">
        <Button disabled={!canIncrease} variant={"primary"} onClick={onIncrease}>
          Increase Lock Amount
        </Button>
      </div>
    </Card.Container>
  )
}
